import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ShoppingCart, Trash2, ArrowLeft, FilterX, Calendar, Download, Heart, FileDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useMenu } from '@/contexts/MenuContext';
import { useRecipes } from '@/contexts/RecipesContext';
import { useReactToPrint } from 'react-to-print';
import Layout from '@/components/Layout';
import { format, addDays, startOfWeek, isBefore, isAfter, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ShoppingItem {
  id: string;
  name: string;
  checked: boolean;
  category?: string;
  normalizedName?: string; // Add normalized name property
  quantity?: number;
  unit?: string;
  originalNames?: string[]; // To track original items that were merged
}

const CATEGORIES = {
  VEGETABLES: 'Verduras',
  CEREALS: 'Cereales',
  LEGUMES: 'Legumbres',
  FRUITS: 'Frutas',
  NUTS: 'Frutos secos/semillas',
  OTHERS: 'Otros'
};

// Ingredient normalization function
const normalizeIngredientName = (name: string): string => {
  let normalized = name.toLowerCase()
    // Remove quantities and units
    .replace(/^\d+(\.\d+)?\s*(g|gr|ml|l|kg|oz|cup|cups|tbsp|tsp)?\s+/i, '')
    // Remove common prefixes
    .replace(/^de\s+|^del\s+|^la\s+|^el\s+|^los\s+|^las\s+/i, '')
    // Standardize common ingredients
    .replace(/^copos\s+de\s+avena|^avena|^hojuelas\s+de\s+avena/i, 'avena')
    .replace(/^porotos|^frijoles|^judías|^alubias/i, 'frijoles')
    .replace(/^garbanzos|^garbanzo/i, 'garbanzos')
    .replace(/^lentejas|^lenteja/i, 'lentejas')
    .replace(/^tomates|^tomate/i, 'tomate')
    .replace(/^cebolla|^cebollas/i, 'cebolla')
    .replace(/^ajo|^ajos/i, 'ajo')
    .replace(/^zanahoria|^zanahorias/i, 'zanahoria')
    .replace(/^manzana|^manzanas/i, 'manzana')
    .replace(/^pera|^peras/i, 'pera')
    .replace(/^arroz/i, 'arroz')
    .replace(/^pan|^panes/i, 'pan')
    .replace(/^pasta/i, 'pasta')
    .trim();
  
  return normalized;
};

// Extract quantity and unit from item name
const extractQuantityAndUnit = (name: string): { name: string, quantity: number | null, unit: string | null } => {
  const match = name.match(/^(\d+(?:\.\d+)?)\s*([a-zA-Z]+)?\s+(.+)$/);
  
  if (match) {
    const quantity = parseFloat(match[1]);
    const unit = match[2] || '';
    const itemName = match[3];
    return { name: itemName, quantity, unit };
  }
  
  return { name, quantity: null, unit: null };
};

// Food item categorization
const categorizeItem = (itemName: string): string => {
  const nameLower = itemName.toLowerCase();
  
  // Vegetables
  if (/tomate|cebolla|ajo|pimiento|zanahoria|pepino|lechuga|espinaca|calabacín|berenjena|brócoli|apio|col|coliflor|espárrago|patata|papa|nabo|remolacha|rúcula|calabaza|puerro|champiñón|seta|aguacate/i.test(nameLower)) {
    return CATEGORIES.VEGETABLES;
  }
  
  // Cereals (including bread, pasta, etc.)
  if (/arroz|pan|pasta|quinoa|trigo|avena|centeno|cebada|espagueti|fideos|macarrones|cereales|couscous|sémola|harina|galleta|tostada|tortita|muesli|bulgur|tortilla|wrap|pita|masa|gofio|tapioca|polenta/i.test(nameLower)) {
    return CATEGORIES.CEREALS;
  }
  
  // Legumes
  if (/lenteja|garbanzo|alubia|judía|frijol|habichuela|soja|guisante|haba|edamame|tempeh|hummus|tofu|lentejas|garbanzos|fabada|habas/i.test(nameLower)) {
    return CATEGORIES.LEGUMES;
  }
  
  // Fruits
  if (/manzana|naranja|plátano|banana|pera|melón|sandía|uva|fresa|frambuesa|arándano|mora|melocotón|durazno|albaricoque|ciruela|cereza|piña|kiwi|mango|lima|limón|papaya|granada|higo|coco|dátil|mandarina|nectarina|membrillo|fruta/i.test(nameLower)) {
    return CATEGORIES.FRUITS;
  }
  
  // Nuts and seeds
  if (/nuez|almendra|cacahuete|maní|anacardo|pistacho|avellana|semilla|chía|lino|sésamo|girasol|calabaza|hemp|cáñamo|piñón|macadamia|pacana|castaña|crema de/i.test(nameLower)) {
    return CATEGORIES.NUTS;
  }
  
  // Default to Others
  return CATEGORIES.OTHERS;
};

// Function to merge similar items
const mergeItems = (items: ShoppingItem[]): ShoppingItem[] => {
  const itemMap: Record<string, ShoppingItem> = {};
  
  items.forEach(item => {
    // Extract quantity and unit
    const { name, quantity, unit } = extractQuantityAndUnit(item.name);
    // Normalize the ingredient name
    const normalizedName = normalizeIngredientName(name);
    
    if (itemMap[normalizedName]) {
      // Item exists, merge it
      const existingItem = itemMap[normalizedName];
      
      // If both items have quantities, add them
      if (quantity && existingItem.quantity) {
        existingItem.quantity += quantity;
      } 
      
      // Track original names
      if (!existingItem.originalNames) {
        existingItem.originalNames = [existingItem.name];
      }
      existingItem.originalNames.push(item.name);
      
      // Use checked status if any item is checked
      if (item.checked) {
        existingItem.checked = true;
      }
    } else {
      // New item, add it to the map
      itemMap[normalizedName] = {
        ...item,
        normalizedName,
        quantity: quantity || undefined,
        unit: unit || undefined
      };
    }
  });
  
  // Convert the map back to an array and format the names
  return Object.values(itemMap).map(item => {
    // Format the name with combined quantity if available
    if (item.quantity && item.unit) {
      return {
        ...item,
        name: `${item.quantity} ${item.unit} ${item.normalizedName}`
      };
    } else {
      return item;
    }
  });
};

const ShoppingList = () => {
  const { shoppingList, weeklyMenu, toggleShoppingItem, refreshMenu } = useMenu();
  const { recipes } = useRecipes();
  const [newItem, setNewItem] = useState('');
  const [newItemCategory, setNewItemCategory] = useState(CATEGORIES.OTHERS);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [shoppingItems, setShoppingItems] = useState<ShoppingItem[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isFiltered, setIsFiltered] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [shouldMergeItems, setShouldMergeItems] = useState(true); // Toggle for item merging
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const shoppingListRenderId = useRef<string>('');
  const lastUpdateTimestamp = useRef<number>(0);

  // Load items from localStorage on mount - using useCallback to prevent unnecessary re-renders
  const loadFromLocalStorage = useCallback(() => {
    const storedItems = localStorage.getItem('shoppingList');
    if (storedItems) {
      const parsedItems = JSON.parse(storedItems);
      // Add category to old items if missing
      const itemsWithCategories = parsedItems.map((item: ShoppingItem) => {
        if (!item.category) {
          return { ...item, category: categorizeItem(item.name) };
        }
        return item;
      });
      
      // Apply merging if enabled
      const processedItems = shouldMergeItems 
        ? mergeItems(itemsWithCategories)
        : itemsWithCategories;
        
      setShoppingItems(processedItems);
    }
  }, [shouldMergeItems]);

  // Load items from localStorage on mount
  useEffect(() => {
    loadFromLocalStorage();
  }, [loadFromLocalStorage]);

  // Save items to localStorage when they change
  useEffect(() => {
    localStorage.setItem('shoppingList', JSON.stringify(shoppingItems));
  }, [shoppingItems]);

  // Update items when shoppingList from context changes with throttling
  useEffect(() => {
    // Add throttling to prevent excessive updates
    const now = Date.now();
    if (now - lastUpdateTimestamp.current < 2000) {
      // Skip this update if less than 2 seconds since last update
      return;
    }

    // Generate a unique ID for this render to prevent duplicate processing
    const currentRenderIdStr = shoppingList ? JSON.stringify(Object.keys(shoppingList.items).length) : '';
    
    // Skip if we've already processed this exact shopping list
    if (currentRenderIdStr && currentRenderIdStr === shoppingListRenderId.current) {
      return;
    }
    
    shoppingListRenderId.current = currentRenderIdStr;
    lastUpdateTimestamp.current = now;
    
    if (shoppingList && Object.keys(shoppingList.items).length > 0) {
      const menuItems = Object.entries(shoppingList.items).map(([id, item]) => ({
        id,
        name: `${Number(item.amount).toFixed(0)} ${item.unit} ${item.name}`,
        checked: item.checked || false,
        category: categorizeItem(`${item.name}`)
      }));
      
      // Apply merging if enabled
      const processedItems = shouldMergeItems 
        ? mergeItems(menuItems)
        : menuItems;
        
      setShoppingItems(processedItems);
      
      // Limit logging in development to reduce console spam
      if (process.env.NODE_ENV === 'development' && now % 10000 < 100) { // Log roughly every 10 seconds
        console.log("Updated shopping items from context:", processedItems);
      }
    } else if (shoppingList && Object.keys(shoppingList.items).length === 0) {
      const storedItems = localStorage.getItem('shoppingList');
      if (storedItems) {
        const parsedItems = JSON.parse(storedItems);
        if (parsedItems.length === 0) {
          setShoppingItems([]);
        }
      } else {
        setShoppingItems([]);
      }
    }
  }, [shoppingList, shouldMergeItems]);

  const addItem = () => {
    if (newItem.trim() !== '') {
      const newItemObject = {
        id: Date.now().toString(),
        name: newItem,
        checked: false,
        category: newItemCategory
      };
      
      if (shouldMergeItems) {
        // Check if similar item exists before adding
        const normalizedNewItem = normalizeIngredientName(newItem);
        const existingItem = shoppingItems.find(item => 
          item.normalizedName === normalizedNewItem || 
          normalizeIngredientName(item.name) === normalizedNewItem
        );
        
        if (existingItem) {
          // Merge with existing item
          const { quantity: newQuantity } = extractQuantityAndUnit(newItem);
          const updatedItems = shoppingItems.map(item => {
            if (item.id === existingItem.id) {
              const { quantity: existingQuantity } = extractQuantityAndUnit(item.name);
              const totalQuantity = (existingQuantity || 0) + (newQuantity || 0);
              
              // Create updated item with combined quantity
              const updatedName = newQuantity 
                ? item.name.replace(/^\d+(\.\d+)?/, totalQuantity.toString())
                : item.name;
                
              return { 
                ...item, 
                name: updatedName,
                quantity: totalQuantity || undefined,
                originalNames: [
                  ...(item.originalNames || [item.name]),
                  newItem
                ]
              };
            }
            return item;
          });
          
          setShoppingItems(updatedItems);
          toast.success(`Añadido a ${existingItem.name}`);
        } else {
          // Add as new item
          setShoppingItems(items => [...items, newItemObject]);
        }
      } else {
        // Add without merging
        setShoppingItems(items => [...items, newItemObject]);
      }
      
      setNewItem('');
    }
  };

  const deleteItem = (id: string) => {
    setShoppingItems(shoppingItems.filter((item) => item.id !== id));
  };

  const toggleItem = async (id: string): Promise<void> => {
    setShoppingItems((items) => 
      items.map((item) => 
        item.id === id ? { ...item, checked: !item.checked } : item
      )
    );
    
    if (shoppingList && shoppingList.items[id]) {
      await toggleShoppingItem(id);
    }
    
    return Promise.resolve();
  };

  const clearList = async () => {
    try {
      // First clear the local state
      setShoppingItems([]);
      
      // Clear local storage items
      const userId = localStorage.getItem('userId');
      if (userId) {
        localStorage.removeItem(`shoppingList_${userId}`);
      } else {
        localStorage.removeItem('shoppingList');
      }
      
      // Important: Clear the shopping list in MenuContext
      if (refreshMenu) {
        try {
          // Force a full refresh of the menu which will recreate the shopping list from scratch
          await refreshMenu();
          toast.success('Lista de compra borrada exitosamente');
        } catch (error) {
          console.error("Error refreshing menu after clearing shopping list:", error);
          toast.error("Error al borrar la lista de compra");
        }
      }
    } catch (error) {
      console.error("Error clearing shopping list:", error);
      toast.error("Error al borrar la lista de compra");
    }
  };

  const clearDateFilter = () => {
    setStartDate(undefined);
    setEndDate(undefined);
    setIsFiltered(false);
    setActiveCategory(null);
    
    if (shoppingList && Object.keys(shoppingList.items).length > 0) {
      const menuItems = Object.entries(shoppingList.items).map(([id, item]) => ({
        id,
        name: `${item.amount} ${item.unit} ${item.name}`,
        checked: item.checked || false
      }));
      
      setShoppingItems(shouldMergeItems ? mergeItems(menuItems) : menuItems);
    }
  };

  const handleFilterByDateRange = async () => {
    if (!startDate || !endDate || !weeklyMenu) {
      toast.error("Por favor selecciona fechas de inicio y fin");
      return;
    }

    setIsFiltering(true);
    try {
      const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      
      const dayMapping: Record<string, { key: string, date: Date }> = {};
      [
        { key: 'monday', name: 'Lunes', date: addDays(currentWeekStart, 0) },
        { key: 'tuesday', name: 'Martes', date: addDays(currentWeekStart, 1) },
        { key: 'wednesday', name: 'Miércoles', date: addDays(currentWeekStart, 2) },
        { key: 'thursday', name: 'Jueves', date: addDays(currentWeekStart, 3) },
        { key: 'friday', name: 'Viernes', date: addDays(currentWeekStart, 4) },
        { key: 'saturday', name: 'Sábado', date: addDays(currentWeekStart, 5) },
        { key: 'sunday', name: 'Domingo', date: addDays(currentWeekStart, 6) }
      ].forEach(day => {
        dayMapping[format(day.date, 'yyyy-MM-dd')] = day;
      });
      
      [
        { key: 'monday2', name: 'Lunes', date: addDays(currentWeekStart, 7) },
        { key: 'tuesday2', name: 'Martes', date: addDays(currentWeekStart, 8) },
        { key: 'wednesday2', name: 'Miércoles', date: addDays(currentWeekStart, 9) },
        { key: 'thursday2', name: 'Jueves', date: addDays(currentWeekStart, 10) },
        { key: 'friday2', name: 'Viernes', date: addDays(currentWeekStart, 11) },
        { key: 'saturday2', name: 'Sábado', date: addDays(currentWeekStart, 12) },
        { key: 'sunday2', name: 'Domingo', date: addDays(currentWeekStart, 13) }
      ].forEach(day => {
        dayMapping[format(day.date, 'yyyy-MM-dd')] = day;
      });

      const filteredDays: string[] = [];
      Object.entries(dayMapping).forEach(([dateStr, dayInfo]) => {
        const date = new Date(dateStr);
        if (
          (isBefore(date, endDate) || isSameDay(date, endDate)) && 
          (isAfter(date, startDate) || isSameDay(date, startDate))
        ) {
          filteredDays.push(dayInfo.key);
        }
      });

      if (filteredDays.length === 0) {
        toast.error("No hay días seleccionados en el rango de fechas");
        setIsFiltering(false);
        return;
      }
      
      const recipeIds: Set<string> = new Set();
      const recipePortion: Map<string, number> = new Map();
      
      filteredDays.forEach(day => {
        const dayMeals = weeklyMenu.days[day];
        if (dayMeals) {
          Object.values(dayMeals).forEach(meals => {
            if (meals && Array.isArray(meals)) {
              meals.forEach(meal => {
                if (meal.recipeId) {
                  recipeIds.add(meal.recipeId);
                  const currentPortion = recipePortion.get(meal.recipeId) || 0;
                  recipePortion.set(meal.recipeId, currentPortion + (meal.portionSize || 1));
                }
              });
            }
          });
        }
      });
      
      if (shoppingList && recipes && recipeIds.size > 0) {
        const filteredItems: Record<string, boolean> = {};
        
        for (const recipeId of recipeIds) {
          const recipe = recipes.find(r => r.id === recipeId);
          const portion = recipePortion.get(recipeId) || 1;
          
          if (recipe) {
            recipe.ingredients.forEach(ingredient => {
              const ingredientKey = `${ingredient.name}-${ingredient.unit}`;
              filteredItems[ingredientKey] = true;
            });
          }
        }
        
        const filteredShoppingItems = Object.entries(shoppingList.items)
          .filter(([id]) => filteredItems[id])
          .map(([id, item]) => ({
            id,
            name: `${item.amount} ${item.unit} ${item.name}`,
            checked: item.checked || false
          }));
        
        setShoppingItems(filteredShoppingItems);
        setIsFiltered(true);
        
        toast.success(`Lista filtrada para ${format(startDate, 'd MMM', { locale: es })} - ${format(endDate, 'd MMM', { locale: es })}`);
      } else {
        setShoppingItems([]);
        setIsFiltered(true);
        toast.info("No hay ingredientes para los días seleccionados");
      }
    } catch (error) {
      console.error("Error filtering shopping list:", error);
      toast.error('Error al filtrar la lista de compras');
    } finally {
      setIsFiltering(false);
    }
  };

  const handlePrint = useReactToPrint({
    documentTitle: 'Lista de Compra Guía Vegana',
    onPrintError: (error) => {
      console.error('Error al imprimir:', error);
      toast.error('Error al imprimir la lista de compra');
    },
    onAfterPrint: () => {
      toast.success('Lista de compra descargada');
    },
    contentRef: printRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 16mm;
      }
      @media print {
        body {
          font-family: sans-serif;
        }
        .print-header {
          text-align: center;
          margin-bottom: 20px;
        }
        .print-header h1 {
          font-size: 24px;
          color: #4CAF50;
        }
        .print-date {
          font-size: 14px;
          color: #666;
          margin-bottom: 20px;
        }
        .print-item {
          padding: 8px 0;
          border-bottom: 1px solid #eee;
          display: flex;
        }
        .print-checkbox {
          width: 20px;
          height: 20px;
          border: 1px solid #ccc;
          border-radius: 3px;
          margin-right: 10px;
        }
        .print-text {
          flex: 1;
        }
      }
    `,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        duration: 0.5
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  // Group items by category
  const categorizedItems = useMemo(() => {
    const result = shoppingItems.reduce((acc: Record<string, ShoppingItem[]>, item) => {
      const category = item.category || CATEGORIES.OTHERS;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});
    
    // Sort each category's items by checked status and then alphabetically
    Object.keys(result).forEach((category) => {
      result[category].sort((a, b) => {
        // First sort by checked status
        if (a.checked !== b.checked) {
          return a.checked ? 1 : -1;
        }
        // Then sort alphabetically
        return a.name.localeCompare(b.name);
      });
    });
    
    return result;
  }, [shoppingItems]);
  
  // Memoize category counts to prevent recalculation
  const categoryCount = useMemo(() => {
    return Object.keys(categorizedItems).reduce((acc: Record<string, number>, category) => {
      acc[category] = categorizedItems[category]?.length || 0;
      return acc;
    }, {});
  }, [categorizedItems]);
  
  // Filter items to show based on active category - memoize this calculation
  const displayItems = useMemo(() => {
    return activeCategory
      ? categorizedItems[activeCategory] || []
      : shoppingItems;
  }, [activeCategory, categorizedItems, shoppingItems]);

  const allCategories = Object.values(CATEGORIES);
  
  // Limit console logging to reduce spamming
  useEffect(() => {
    const now = Date.now();
    if (process.env.NODE_ENV === 'development' && now % 10000 < 100) { // Log roughly every 10 seconds
      console.log("Current shopping items:", shoppingItems);
      console.log("Shopping list from context:", shoppingList);
    }
  }, [shoppingItems, shoppingList]);

  return (
    <Layout>
      <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-white">
        <div className="w-full py-12 px-4 container mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="space-y-8 mx-auto"
          >
            {/* Header section */}
            <motion.div variants={itemVariants} className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-4xl font-bold text-vegana-primary mb-2">Lista de Compra</h1>
                
                {/* Lisa advice box */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white/90 backdrop-blur-md border border-purple-100 rounded-xl shadow-md p-4 mt-4 relative overflow-hidden"
                >
                  <div className="absolute top-0 left-0 h-full w-1/2 bg-gradient-to-r from-purple-100/20 to-transparent z-0"></div>
                  <div className="absolute top-0 right-0 h-full w-1/2 bg-gradient-to-l from-purple-200/20 to-transparent z-0"></div>
                  
                  <div className="relative z-10 flex items-start gap-4">
                    <div className="flex-1">
                      <div className={`${isMobile ? 'block mb-3 text-center' : 'hidden'}`}>
                        <Avatar className="h-14 w-14 border-2 border-purple-300 shadow-lg inline-block bg-white">
                          <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                          <AvatarFallback>L</AvatarFallback>
                        </Avatar>
                      </div>
                      
                      <p className="text-gray-600 text-center sm:text-left text-sm leading-tight px-1">
                        Para que no pienses en nada y solo disfrutes, te prepararé la lista de la compra de todas las recetas de tu menú semanal. Solo tienes que seleccionar los días y mágicamente aparecerán todos los ingredientes. También puedes añadir a tu lista de la compra otros ingredientes que necesitas. Si para ti es más cómodo, puedes descargar la lista en PDF <Heart className="inline-block h-4 w-4 text-vegana-primary" />
                      </p>
                    </div>
                    
                    <div className={`flex justify-center ${isMobile ? 'hidden' : 'block'}`}>
                      <Avatar className="h-14 w-14 border-2 border-purple-300 shadow-lg flex-shrink-0 bg-white">
                        <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
                        <AvatarFallback>L</AvatarFallback>
                      </Avatar>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Navigation and options section */}
            <motion.div variants={itemVariants} className="mb-6">
              <Link to="/menu-semanal" className="flex items-center text-vegana-primary hover:text-vegana-secondary mb-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver al menú semanal
              </Link>

              <div className="flex flex-wrap gap-3 mb-4">
                {shoppingItems.length > 0 && (
                  <Button
                    onClick={() => handlePrint()}
                    variant="outline"
                    className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary hover:text-white"
                  >
                    <FileDown className="h-4 w-4 mr-2" />
                    Descargar PDF
                  </Button>
                )}
                
                {/* Toggle for item merging */}
                <div className="flex items-center space-x-2 ml-auto">
                  <Checkbox 
                    id="merge-items" 
                    checked={shouldMergeItems} 
                    onCheckedChange={(checked) => setShouldMergeItems(checked as boolean)}
                  />
                  <label 
                    htmlFor="merge-items" 
                    className="text-sm text-gray-700 cursor-pointer"
                  >
                    Agrupar ingredientes similares
                  </label>
                </div>
              </div>
              
              {/* Date filter section */}
              <div className="bg-white border rounded-lg p-4 mb-6">
                <h3 className="text-md font-medium mb-3 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-vegana-primary" />
                  Filtrar por fechas
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label className="text-sm text-gray-600 mb-1 block">Fecha inicio</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left ${!startDate && "text-gray-400"}`}
                        >
                          {startDate ? format(startDate, 'd MMM yyyy', { locale: es }) : "Selecciona fecha"}
                          <Calendar className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          className="p-3 pointer-events-auto"
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="flex-1">
                    <label className="text-sm text-gray-600 mb-1 block">Fecha fin</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={`w-full justify-start text-left ${!endDate && "text-gray-400"}`}
                        >
                          {endDate ? format(endDate, 'd MMM yyyy', { locale: es }) : "Selecciona fecha"}
                          <Calendar className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          disabled={date => !startDate || isBefore(date, startDate)}
                          className="p-3 pointer-events-auto"
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="flex items-end gap-2">
                    <Button 
                      onClick={handleFilterByDateRange} 
                      disabled={!startDate || !endDate || isFiltering}
                      className="bg-vegana-primary text-white"
                    >
                      {isFiltering ? 'Filtrando...' : 'Filtrar'}
                    </Button>
                    
                    {isFiltered && (
                      <Button
                        onClick={clearDateFilter}
                        variant="ghost"
                        className="text-gray-500"
                      >
                        <FilterX className="h-4 w-4 mr-1" />
                        Limpiar
                      </Button>
                    )}
                  </div>
                </div>
                
                {isFiltered && startDate && endDate && (
                  <div className="mt-3 text-sm text-vegana-primary bg-vegana-primary/10 p-2 rounded-md">
                    Mostrando lista de compra para: {format(startDate, 'd MMM', { locale: es })} - {format(endDate, 'd MMM', { locale: es })}
                  </div>
                )}
              </div>
              
              <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded-md">
                <p className="text-sm text-amber-800">
                  <strong>Información:</strong> La lista de compras se actualiza automáticamente cuando añades o eliminas recetas de tu menú semanal.
                  Usa el filtro de fechas para ver solo los ingredientes de los días que te interesan.
                </p>
              </div>
            </motion.div>

            {/* Add new item form */}
            <motion.div variants={itemVariants} className="flex items-center space-x-4">
              <div className="flex-grow">
                <Input
                  type="text"
                  placeholder="Añadir un nuevo producto"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  className="w-full"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addItem();
                    }
                  }}
                />
              </div>
              
              <select 
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vegana-primary"
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
              >
                {Object.values(CATEGORIES).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              
              <Button onClick={addItem} className="bg-vegana-primary text-white hover:bg-vegana-secondary">
                Añadir
              </Button>
            </motion.div>

            {/* Shopping list display */}
            {shoppingItems.length > 0 ? (
              <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-800 flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Productos ({shoppingItems.length})
                </h2>
                
                {/* Category filters */}
                <Tabs defaultValue="all" className="w-full">
                  <TabsList className="mb-6 flex flex-wrap gap-1 bg-gray-100/80 p-1 rounded-lg">
                    <TabsTrigger 
                      value="all" 
                      onClick={() => setActiveCategory(null)}
                      className={`text-sm px-3 py-1 ${!activeCategory ? 'bg-white shadow-sm' : ''}`}
                    >
                      Todos ({shoppingItems.length})
                    </TabsTrigger>
                    
                    {allCategories.map(category => (
                      categoryCount[category] > 0 && (
                        <TabsTrigger
                          key={category}
                          value={category}
                          onClick={() => setActiveCategory(category)}
                          className={`text-sm px-3 py-1 ${activeCategory === category ? 'bg-white shadow-sm' : ''}`}
                        >
                          {category} ({categoryCount[category] || 0})
                        </TabsTrigger>
                      )
                    ))}
                  </TabsList>
                  
                  {/* TabsContent sections to match the TabsTriggers */}
                  <TabsContent value="all">
                    {/* Content will be controlled by the activeCategory state */}
                  </TabsContent>
                  
                  {allCategories.map(category => (
                    categoryCount[category] > 0 && (
                      <TabsContent key={category} value={category}>
                        {/* Content will be controlled by the activeCategory state */}
                      </TabsContent>
                    )
                  ))}
                </Tabs>
                
                <div ref={printRef} className="w-full">
                  {/* Print header - for printing only */}
                  <div className="hidden print:block print-header">
                    <h1>Lista de Compra - Guía Vegana</h1>
                    <p className="print-date">
                      Fecha: {new Date().toLocaleDateString('es-ES')}
                      {isFiltered && startDate && endDate && (
                        <span> (Filtrado: {format(startDate, 'd MMM', { locale: es })} - {format(endDate, 'd MMM', { locale: es })})</span>
                      )}
                    </p>
                  </div>
                  
                  {/* For printing - always show by categories */}
                  <div className="hidden print:block">
                    {Object.entries(categorizedItems).map(([category, items]) => (
                      items.length > 0 && (
                        <div key={category} className="mb-6">
                          <h3 className="text-lg font-medium mb-2 border-b pb-1">{category}</h3>
                          <ul className="divide-y divide-gray-200">
                            {items.map((item) => (
                              <li key={item.id} className="py-2 flex items-center">
                                <div className="h-4 w-4 border mr-2 print-checkbox"></div>
                                <span className="print-text">{item.name}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    ))}
                  </div>
                  
                  {/* For screen display - show based on active filter */}
                  <div className="print:hidden">
                    {activeCategory ? (
                      <ul className="divide-y divide-gray-200 w-full">
                        {(categorizedItems[activeCategory] || []).map((item) => (
                          <li
                            key={item.id}
                            className="py-3 flex items-center justify-between"
                          >
                            <div className="flex items-center flex-grow">
                              <Checkbox
                                id={item.id}
                                checked={item.checked}
                                onCheckedChange={() => toggleItem(item.id)}
                                className="mr-3"
                              />
                              <label 
                                htmlFor={item.id} 
                                className={`text-gray-700 ${item.checked ? 'line-through text-gray-400' : ''}`}
                              >
                                {item.name}
                                {item.originalNames && item.originalNames.length > 1 && (
                                  <span className="text-xs text-gray-500 ml-2">
                                    (agrupado de {item.originalNames.length} productos)
                                  </span>
                                )}
                              </label>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteItem(item.id)}
                              className="hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      Object.entries(categorizedItems).map(([category, items]) => (
                        items.length > 0 && (
                          <div key={category} className="mb-6">
                            <h3 className="text-lg font-medium mb-2 text-vegana-primary bg-vegana-primary/5 px-3 py-1 rounded-md">
                              {category} ({items.length})
                            </h3>
                            <ul className="divide-y divide-gray-200">
                              {items.map((item) => (
                                <li
                                  key={item.id}
                                  className="py-3 flex items-center justify-between"
                                >
                                  <div className="flex items-center flex-grow">
                                    <Checkbox
                                      id={item.id}
                                      checked={item.checked}
                                      onCheckedChange={() => toggleItem(item.id)}
                                      className="mr-3"
                                    />
                                    <label 
                                      htmlFor={item.id} 
                                      className={`text-gray-700 ${item.checked ? 'line-through text-gray-400' : ''}`}
                                    >
                                      {item.name}
                                      {item.originalNames && item.originalNames.length > 1 && (
                                        <span className="text-xs text-gray-500 ml-2">
                                          (agrupado de {item.originalNames.length} productos)
                                        </span>
                                      )}
                                    </label>
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => deleteItem(item.id)}
                                    className="hover:text-red-500"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-center">
                  <Button 
                    onClick={clearList} 
                    variant="destructive" 
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Limpiar Lista
                  </Button>
                </div>
              </motion.div>
            ) : (
              <motion.div variants={itemVariants} className="text-center py-12 bg-white rounded-lg shadow-sm">
                <ShoppingCart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-medium text-gray-600 mb-2">Tu lista de compra está vacía</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-8">
                  Añade productos manualmente o genera la lista basada en tu menú semanal.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link to="/menu-semanal">
                    <Button 
                      variant="outline" 
                      className="border-vegana-primary text-vegana-primary hover:bg-vegana-primary hover:text-white w-full sm:w-auto"
                    >
                      Ir al menú semanal
                    </Button>
                  </Link>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default ShoppingList;
